import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, Loader2, Trash2 } from "lucide-react";
import { useComments, useCreateComment, useDeleteComment } from "./use-comments";

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function DirectoryComments({
  entityType,
  entityId,
  entityName,
}: {
  entityType: "person" | "vendor";
  entityId: number | undefined;
  entityName: string;
}) {
  const { data: comments, isLoading } = useComments(entityType, entityId);
  const createComment = useCreateComment(entityType, entityId);
  const deleteComment = useDeleteComment(entityType, entityId);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const count = comments?.length ?? 0;

  // Keep the newest comment in view whenever the thread grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count]);

  function submit() {
    const body = draft.trim();
    if (!body || !entityId || createComment.isPending) return;
    createComment.mutate(
      { body, author: "Yogev" },
      { onSuccess: () => setDraft("") },
    );
  }

  return (
    <div className="w-80 shrink-0 border-l bg-background flex flex-col h-full">
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-blue-500" />
        <span className="text-sm font-semibold text-foreground">Progress log</span>
        {count > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">{count}</span>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : count === 0 ? (
          <div className="text-xs text-muted-foreground text-center py-6">
            No comments yet. Add the first note on {entityName}&rsquo;s progress below.
          </div>
        ) : (
          comments!.map((c) => (
            <div key={String(c.id)} className="group rounded-lg bg-muted/40 px-3 py-2">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-foreground">{c.author}</span>
                <span className="text-[10px] text-muted-foreground">
                  {formatStamp(c.created_at)}
                </span>
                <button
                  type="button"
                  aria-label="Delete comment"
                  title="Delete comment"
                  onClick={() => deleteComment.mutate(c.id)}
                  className="ml-auto p-0.5 rounded text-muted-foreground/50 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap break-words">
                {c.body}
              </p>
            </div>
          ))
        )}
      </div>

      <div className="border-t p-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          placeholder={entityId ? "Add a note…  (Enter to send)" : "Loading…"}
          disabled={!entityId || createComment.isPending}
          className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-50"
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || !entityId || createComment.isPending}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {createComment.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Comment
          </button>
        </div>
      </div>
    </div>
  );
}
